import { classNamePrefix } from '../globals';
import { TChoices, TConfig } from '../types';
import { renderButton } from './button';
import { renderDescription } from './description';
import { renderImage } from './image';
import { renderListButton } from './listButton';
import { renderListService } from './listService';
import { renderModal } from './modal';
import { renderService } from './service';
import { renderTitle } from './title';

export function renderSettings (
    config: TConfig,
    getChoices: () => TChoices,
    sendRecords: () => void,
    setChoice: (serviceName: string, value: boolean | null) => void,
    backCallback: () => void,
    doneCallback: () => void
) {
    const className = classNamePrefix + '-settings';
    const titleId = className + '-title';
    const descriptionId = className + '-description';

    const modalElement = renderModal(titleId, descriptionId);

    const settingsElement = document.createElement('div');
    settingsElement.classList.add(className);

    modalElement.appendChild(settingsElement);

    const headElement = document.createElement('div');
    headElement.classList.add(className + '__head');

    const { title, description, checkAll, uncheckAll } = config.translations[config.language].settings;

    // Title
    const titleElement = renderTitle(title, 2, titleId);
    headElement.appendChild(titleElement);

    // Image
    if (config.images?.settings?.source) {
        const { source, alternative, width, height, alignment } = config.images.settings;

        const imageElement = renderImage(source, alternative, width, height, alignment, className + '__image');
        headElement.appendChild(imageElement);
    }

    // Description
    const descriptionElement = renderDescription(description, descriptionId);
    headElement.appendChild(descriptionElement);

    // Decline all button
    const uncheckAllButtonElement = renderButton(uncheckAll, () => {
        const checkboxElements = settingsElement.querySelectorAll('input') as NodeListOf<HTMLInputElement>;

        checkboxElements.forEach((checkboxElement) => {
            if (checkboxElement.checked) {
                checkboxElement.click();
            }
        });
    }, `${className}__button-uncheck-all`);

    // Accept all button
    const checkAllButtonElement = renderButton(checkAll, () => {
        const checkboxElements = settingsElement.querySelectorAll('input') as NodeListOf<HTMLInputElement>;

        checkboxElements.forEach((checkboxElement) => {
            if (!checkboxElement.checked) {
                checkboxElement.click();
            }
        });
    }, `${className}__button-check-all`);

    // First button list
    const firstButtonListElement = renderListButton([uncheckAllButtonElement, checkAllButtonElement]);
    headElement.appendChild(firstButtonListElement);

    settingsElement.appendChild(headElement);

    const { language, services } = config;
    const { privacyPolicy } = config.translations[language];

    // Services
    if (services) {
        const serviceNames = Object.keys(services);
        const servicesTranslations = config.translations[language].settings?.services;

        if (serviceNames && servicesTranslations) {
            if (serviceNames.length === 1) {
                // Single service
                const serviceName = serviceNames[0];

                const label = servicesTranslations[serviceName].label;
                const description = servicesTranslations[serviceName].description;
                const url = servicesTranslations[serviceName].url;
                const imageSource = services[serviceName].image?.source;
                const required = services[serviceName].required || false;

                if (label && description) {
                    // Service
                    const serviceElement = renderService(
                        serviceName,
                        label,
                        description,
                        privacyPolicy.label,
                        required,
                        getChoices,
                        url,
                        imageSource
                    );
                    settingsElement.appendChild(serviceElement);
                }

            } else {
                // Multiple services
                const serviceElements: HTMLDivElement[] = [];

                serviceNames.forEach((serviceName) => {
                    const label = servicesTranslations[serviceName].label;
                    const description = servicesTranslations[serviceName].description;
                    const required = services[serviceName].required || false;
                    const url = servicesTranslations[serviceName].url;
                    const imageSource = services[serviceName].image?.source;

                    if (label && description) {
                        // Service
                        serviceElements.push(
                            renderService(
                                serviceName,
                                label,
                                description,
                                privacyPolicy.label,
                                required,
                                getChoices,
                                url,
                                imageSource
                            )
                        );
                    }
                });

                // Service list
                const serviceListElement = renderListService(serviceElements);
                settingsElement.appendChild(serviceListElement);
            }
        }
    }

    const { back, done } = config.translations[config.language].settings;

    // Back button
    const backButtonElement = renderButton(back, backCallback);

    // Done button
    const doneButtonElement = renderButton(done, () => {
        const checkboxElements = settingsElement.querySelectorAll('input') as NodeListOf<HTMLInputElement>;

        checkboxElements.forEach((checkboxElement) => {
            setChoice(checkboxElement.name, checkboxElement.checked);
        });

        // Send new records
        sendRecords();

        doneCallback();
    });

    // Second button list
    const secondButtonListElement = renderListButton([backButtonElement, doneButtonElement]);
    settingsElement.appendChild(secondButtonListElement);

    return modalElement;
}